import React from 'react';
import { Selection, SelectionNewVersion } from 'src/domains/sportsbook/webview/components/Selection';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { observer } from 'src/utils/mobx-react';
import { MarketId, SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import {
    HeaderCell,
    LineCell,
    LineGroupedRoot,
    MarketRows,
    SelectionCell,
    SelectionHeader,
} from './LineGroupedMarketGroup.style';
import { SelectionsGroupWrap } from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified.style';
import { sortOverUnderColumnNames } from './sortOverUnderColumnNames';
import { resolveLineHeaderLabel } from './resolveLineHeaderLabel';
import { LineHeaderLabel } from './LineHeaderLabel';
import { LockIcon } from 'src/ui/assets/icons/other/LockIcon';
import {
    isSignedLineMarket,
    isSignedLineMarketName,
    resolveSignedLineLabel,
} from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/lineMarkets';

interface PropsType {
    name: string;
    markets: Array<MarketId>;
    marketItems: Array<EventMarketItemType>;
    selections: Record<number | string, SelectionModel[]>;
    isOpen: boolean;
    onClick(): void;
    marketId: MarketId;
    suspendedMarketIds?: ReadonlySet<number>;
}

const INITIAL_ROWS_LIMIT = 4;
const FALLBACK_SELECTION_COLUMNS = 2;

interface LineGroupedRowType {
    key: string;
    marketOldId: number;
    line: number | null;
    selectionIds: Array<SelectionId>;
    selectionNames: Array<string>;
    forceSuspended: boolean;
}

const resolveMarketSelections = (
    marketId: MarketId,
    marketModel: MarketModel,
    selections: Record<number | string, SelectionModel[]>
): SelectionModel[] => {
    const fromProps = selections[marketModel.id];
    if (fromProps !== undefined && fromProps.length > 0) {
        return fromProps;
    }

    const fromRawId = selections[marketId.toOldId()];
    if (fromRawId !== undefined && fromRawId.length > 0) {
        return fromRawId;
    }

    return marketModel.selections.filter((selection) => selection.display);
};

const getMarketRows = (
    groupName: string,
    markets: Array<MarketId>,
    marketItems: Array<EventMarketItemType>,
    selections: Record<number | string, SelectionModel[]>,
    suspendedMarketIds: ReadonlySet<number> | undefined,
    hideSuspendedLines: boolean
): LineGroupedRowType[] => {
    const itemsById = new Map(marketItems.map((item) => [item.id2.toOldId(), item]));

    return markets
        .map((marketId, index): LineGroupedRowType | null => {
            const marketOldId = marketId.toOldId();
            const item = itemsById.get(marketOldId);
            const forceSuspended = suspendedMarketIds?.has(marketOldId) === true;
            const marketModel = marketId.getModel();

            if (marketModel === null) {
                // Keep the slot mounted while suspended / model still loading — never drop the row.
                return {
                    key: `${groupName}-slot-${index}`,
                    marketOldId,
                    line: item?.keyLine ?? null,
                    selectionIds: [],
                    selectionNames: [],
                    forceSuspended: true,
                };
            }

            // Prematch (GLOB-2863): hide inactive sibling lines. In-play / sticky coalesce keeps the row.
            if (hideSuspendedLines === true && forceSuspended === false && marketModel.activeAndDisplayed === false) {
                return null;
            }

            const marketSelections = resolveMarketSelections(marketId, marketModel, selections);

            return {
                key: `${groupName}-slot-${index}`,
                marketOldId,
                line: marketModel.line ?? item?.keyLine ?? null,
                selectionIds: marketSelections.map((selection) => selection.id2),
                selectionNames: marketSelections.map((selection) => selection.name),
                forceSuspended,
            };
        })
        .filter((row): row is LineGroupedRowType => row !== null)
        .sort((a, b) => {
            if (a.line === null && b.line === null) {
                return 0;
            }
            if (a.line === null) {
                return 1;
            }
            if (b.line === null) {
                return -1;
            }
            return a.line - b.line;
        })
        .map((row, index) => ({
            ...row,
            key: `${groupName}-slot-${index}`,
        }));
};

const lineLabel = (line: number | null): string => {
    if (line === null) {
        return '-';
    }
    return Number.isInteger(line) ? `${line}.0` : `${line}`;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripLineFromSelectionName = (name: string, line: number | null): string => {
    if (line === null) {
        return name;
    }

    const lineVariants = new Set([String(line)]);
    if (Number.isInteger(line)) {
        lineVariants.add(`${line}.0`);
    }

    let result = name;
    for (const lineStr of lineVariants) {
        const stripped = result.replace(new RegExp(`\\s*[+\\-]?${escapeRegExp(lineStr)}\\s*$`), '');
        if (stripped !== result) {
            result = stripped;
            break;
        }
    }

    if (result === name) {
        result = name.replace(/\s*[+\-]?\d+(?:\.\d+)?\s*$/, '');
    }

    return result.trim();
};

const findSelectionIdForColumn = (row: LineGroupedRowType, columnName: string): SelectionId | undefined => {
    const selectionIndex = row.selectionNames.findIndex(
        (selectionName) => stripLineFromSelectionName(selectionName, row.line) === columnName
    );

    return selectionIndex !== -1 ? row.selectionIds[selectionIndex] : undefined;
};

const SuspendedPlaceholder = (): JSX.Element => <LockIcon className='selection-suspended--icon' />;

const resolveIsSignedLine = (firstMarketModel: MarketModel | null, groupName: string): boolean => {
    if (firstMarketModel !== null) {
        return isSignedLineMarket(firstMarketModel.templateId, firstMarketModel.template.name);
    }

    return isSignedLineMarketName(groupName);
};

export const LineGroupedMarketGroup = observer('LineGroupedMarketGroup', (props: PropsType) => {
    const { name, markets, marketItems, selections, isOpen, onClick, marketId, suspendedMarketIds } = props;

    return (
        <LineGroupedRoot
            className='market-group--line-grouped'
            data-test='line-grouped-market-group'
        >
            <MarketHeader
                name={name}
                isOpen={isOpen}
                onClick={onClick}
                marketId={marketId}
                keepVisibleWhileLoading={true}
            />
            {isOpen ? (
                <MarketBody
                    groupName={name}
                    markets={markets}
                    marketItems={marketItems}
                    selections={selections}
                    suspendedMarketIds={suspendedMarketIds}
                />
            ) : null}
        </LineGroupedRoot>
    );
});

const MarketBody = observer(
    'MarketBody',
    (props: {
        groupName: string;
        markets: Array<MarketId>;
        marketItems: Array<EventMarketItemType>;
        selections: Record<number | string, SelectionModel[]>;
        suspendedMarketIds?: ReadonlySet<number>;
    }) => {
        const [isExpanded, setIsExpanded] = React.useState(false);
        const stableColumnCountRef = React.useRef(FALLBACK_SELECTION_COLUMNS);
        const stableHeaderNamesRef = React.useRef<string[]>([]);

        const { groupName, markets, marketItems, selections, suspendedMarketIds } = props;

        // Prefer a loaded model for template / in-play detection; fall back so we never unmount mid-swap.
        const firstMarketModel = markets.map((marketId) => marketId.getModel()).find((model) => model !== null) ?? null;
        const isInPlay = firstMarketModel?.eventModel?.timeSettingsStarted === true;
        const hideSuspendedLines = isInPlay === false;

        const rows = getMarketRows(groupName, markets, marketItems, selections, suspendedMarketIds, hideSuspendedLines);

        if (markets.length === 0 || rows.length === 0) {
            return null;
        }

        const isSignedLine = resolveIsSignedLine(firstMarketModel, groupName);
        const lineHeaderLabel = isSignedLine
            ? null
            : resolveLineHeaderLabel(
                  groupName,
                  firstMarketModel?.templateId ?? '',
                  firstMarketModel?.template.name ?? ''
              );

        const visibleRows = isExpanded === true ? rows : rows.slice(0, INITIAL_ROWS_LIMIT);
        const headerRow = rows.find((row) => row.selectionNames.length > 0) ?? rows[0];
        const headerLine = headerRow?.line ?? null;
        const nextHeaderNames = sortOverUnderColumnNames(
            (headerRow?.selectionNames ?? []).map((selectionName) =>
                stripLineFromSelectionName(selectionName, headerLine)
            )
        );

        if (nextHeaderNames.length > 0) {
            stableHeaderNamesRef.current = nextHeaderNames;
        }

        const headerSelectionNames = nextHeaderNames.length > 0 ? nextHeaderNames : stableHeaderNamesRef.current;

        const nextSelectionColumnCount = Math.max(
            ...rows.map((row) => row.selectionIds.length),
            headerSelectionNames.length,
            0
        );

        if (nextSelectionColumnCount > 0) {
            stableColumnCountRef.current = nextSelectionColumnCount;
        }

        const selectionColumnCount =
            nextSelectionColumnCount > 0 ? nextSelectionColumnCount : stableColumnCountRef.current;
        const columnCount = isSignedLine ? selectionColumnCount : selectionColumnCount + 1;

        const toggleExpand = (e: React.MouseEvent): void => {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
        };

        const renderSelection = (row: LineGroupedRowType, index: number) => {
            const selectionId = findSelectionIdForColumn(row, headerSelectionNames[index] ?? '');
            const selectionModel = selectionId?.getModel();
            const lineLabel = resolveSignedLineLabel(selectionModel?.templateId, row.line);

            return (
                <SelectionCell key={`${row.key}-selection-${index}`}>
                    {selectionId instanceof SelectionId ? (
                        isSignedLine ? (
                            <SelectionNewVersion
                                selectionId={selectionId}
                                sp={false}
                                lineLabel={lineLabel}
                                forceSuspended={row.forceSuspended}
                            />
                        ) : (
                            <Selection
                                selectionId={selectionId}
                                forceSuspended={row.forceSuspended}
                            />
                        )
                    ) : row.forceSuspended ? (
                        <SuspendedPlaceholder />
                    ) : null}
                </SelectionCell>
            );
        };

        return (
            <>
                {selectionColumnCount > 0 ? (
                    <SelectionHeader>
                        {isSignedLine === false ? (
                            <HeaderCell>
                                {lineHeaderLabel === null ? null : <LineHeaderLabel kind={lineHeaderLabel} />}
                            </HeaderCell>
                        ) : null}
                        {Array.from({ length: selectionColumnCount }, (_, index) => (
                            <HeaderCell key={`header-${index}`}>{headerSelectionNames[index] ?? ''}</HeaderCell>
                        ))}
                    </SelectionHeader>
                ) : null}
                <SelectionsGroupWrap>
                    <MarketRows $columnCount={columnCount}>
                        {visibleRows.map((row) => (
                            <React.Fragment key={row.key}>
                                {isSignedLine === false ? <LineCell>{lineLabel(row.line)}</LineCell> : null}
                                {Array.from({ length: selectionColumnCount }, (_, index) =>
                                    renderSelection(row, index)
                                )}
                            </React.Fragment>
                        ))}
                    </MarketRows>
                    {rows.length > INITIAL_ROWS_LIMIT ? (
                        <ExpandButton
                            isExpanded={isExpanded}
                            toggleExpand={toggleExpand}
                        />
                    ) : null}
                </SelectionsGroupWrap>
            </>
        );
    }
);
